import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Filter, Search, Sparkles, X } from "lucide-react";
import api from "@/lib/api";
import ServerCard from "@/components/ServerCard";
import SEO, { SITE_URL, SITE_NAME } from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { getCategoryIntro } from "@/lib/categoryIntros";
import { useAuth } from "@/context/AuthContext";

export default function Browse({ routeCategory = null, routeFeatured = false }) {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState(params.get("search") || "");
  const { user, loginWithDiscord } = useAuth();
  const login = loginWithDiscord;
  const navigate = useNavigate();

  const PAGE_SIZE = 24;

  const category = routeCategory ? routeCategory.id : params.get("category") || "";
  const sort = params.get("sort") || "trending";
  const featured = routeFeatured || params.get("featured") === "1";
  const search = params.get("search") || "";

  // Legacy /servers?featured=1 → redirect to canonical /servers/featured
  useEffect(() => {
    if (!routeFeatured && !routeCategory && params.get("featured") === "1") {
      const next = new URLSearchParams(params);
      next.delete("featured");
      const qs = next.toString();
      navigate(`/servers/featured${qs ? "?" + qs : ""}`, { replace: true });
    }
  }, [routeFeatured, routeCategory, params, navigate]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/categories");
      setCategories(data);
    })();
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/servers", {
        params: {
          search: search || undefined,
          category: category || undefined,
          sort,
          featured: featured || undefined,
          limit: PAGE_SIZE,
          skip: 0,
        },
      });
      setItems(data.items || []);
      setTotal(Number(data.total || 0));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, category, sort, featured]);

  const loadMore = async () => {
    if (loadingMore || items.length >= total) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/servers", {
        params: {
          search: search || undefined,
          category: category || undefined,
          sort,
          featured: featured || undefined,
          limit: PAGE_SIZE,
          skip: items.length,
        },
      });
      // De-dupe by id in case bumps re-ordered the list between fetches.
      setItems((prev) => {
        const seen = new Set(prev.map((s) => s.id));
        const fresh = (data.items || []).filter((s) => !seen.has(s.id));
        return [...prev, ...fresh];
      });
      setTotal(Number(data.total || total));
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const applyParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "" || value === null || value === undefined) next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    applyParam("search", q.trim());
  };

  const handleBump = async (server) => {
    if (!user) return login();
    try {
      const { data } = await api.post(`/servers/${server.id}/bump`);
      toast.success(`Bumped ${server.name}`);
      setItems((prev) => prev.map((s) => (s.id === server.id ? { ...s, bumped_at: data.bumped_at } : s)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bump failed");
    }
  };

  const activeFilters = useMemo(() => {
    const arr = [];
    if (search) arr.push({ key: "search", label: `“${search}”` });
    if (category) arr.push({ key: "category", label: categories.find((c) => c.id === category)?.name || category });
    if (featured) arr.push({ key: "featured", label: "Featured only" });
    return arr;
  }, [search, category, featured, categories]);

  return (
    <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SEO
        title={
          routeFeatured
            ? "Featured Discord Servers — Handpicked & Promoted"
            : routeCategory
              ? `${routeCategory.name} Discord Servers`
              : search
                ? `Search Discord servers · "${search}"`
                : category
                  ? `${categories.find((c) => c.id === category)?.name || category} Discord Servers`
                  : "Discord Server List — Browse & Find Servers"
        }
        description={
          routeFeatured
            ? "Featured Discord servers on Serverlistr — handpicked communities boosted to the top of the directory. Discover trending gaming, anime, community and tech servers."
            : routeCategory
              ? `Browse the best ${routeCategory.name} Discord servers on Serverlistr. Ranked by rating and member count, updated live from Discord.`
              : search
                ? `Search results for "${search}" — ${items.length} Discord server${items.length === 1 ? "" : "s"} found on Serverlistr. Filter by category, sort, and members.`
                : category
                  ? `Discover the best ${categories.find((c) => c.id === category)?.name || category} Discord servers on Serverlistr. Live member counts, ratings, and instant invites.`
                  : sort === "new"
                    ? "Newest Discord servers on Serverlistr — see what communities were listed today. Fresh gaming, anime, tech and community servers ordered by submission date."
                    : sort === "rating"
                      ? "Highest rated Discord servers on Serverlistr — the top-rated communities based on real member reviews. Discover quality servers verified by the community."
                      : sort === "members"
                        ? "Biggest Discord servers on Serverlistr — the largest active communities ranked by member count. Find popular gaming, anime and community servers."
                        : "Browse every Discord server on Serverlistr. Filter by category, search by name or tag, and sort by trending, rating, newest, or member count."
        }
        path={
          routeFeatured
            ? "/servers/featured"
            : routeCategory
              ? `/servers/category/${routeCategory.id}`
              : "/servers"
        }
        keywords={`discord server list, discord servers, ${routeCategory ? routeCategory.name.toLowerCase() + " discord servers, " : ""}${routeFeatured ? "featured discord servers, promoted discord servers, " : ""}find discord servers, best discord servers, discord communities`}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: routeFeatured
              ? "Featured Discord Servers"
              : routeCategory ? `${routeCategory.name} Discord Servers` : "Discord Server List",
            url: routeFeatured
              ? `${SITE_URL}/servers/featured`
              : routeCategory ? `${SITE_URL}/servers/category/${routeCategory.id}` : `${SITE_URL}/servers`,
            isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
            hasPart: (items || []).slice(0, 12).map((s) => ({
              "@type": "Thing",
              name: s.name,
              url: `${SITE_URL}/servers/${s.slug || s.id}`,
            })),
          },
          // ItemList — Google can render this as a rich list snippet in the SERP.
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: routeFeatured
              ? "Featured Discord Servers"
              : routeCategory ? `Best ${routeCategory.name} Discord Servers` : "Best Discord Servers",
            numberOfItems: items?.length || 0,
            itemListOrder: sort === "new"
              ? "https://schema.org/ItemListOrderDescending"
              : "https://schema.org/ItemListUnordered",
            itemListElement: (items || []).slice(0, 20).map((s, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${SITE_URL}/servers/${s.slug || s.id}`,
              name: s.name,
            })),
          },
        ]}
      />
      <Breadcrumbs
        items={[
          { name: "Discord Servers", path: (routeFeatured || routeCategory || category || featured || search) ? "/servers" : undefined },
          ...(routeFeatured
            ? [{ name: "Featured" }]
            : routeCategory
              ? [{ name: routeCategory.name }]
              : category
                ? [{ name: categories.find((c) => c.id === category)?.name || category }]
                : featured
                  ? [{ name: "Featured" }]
                  : search
                    ? [{ name: `Search: ${search}` }]
                    : []),
        ]}
      />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-neon-cyan mb-2">
            {routeFeatured ? "Featured" : routeCategory ? routeCategory.name : "Discover"}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black">
            {routeFeatured
              ? "Featured Discord servers"
              : routeCategory
                ? `${routeCategory.name} Discord Servers`
                : "Discord server list"}
          </h1>
          <p className="mt-2 text-sm text-zinc-400">
            {routeFeatured
              ? `${items.length} handpicked communities · promoted by their owners.`
              : routeCategory && getCategoryIntro(routeCategory.id)
                ? getCategoryIntro(routeCategory.id).tagline
                : `${items.length} servers · filter by category, sort by trending, rating or member count.`}
          </p>
        </div>
        <form onSubmit={onSearchSubmit} className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="browse-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, description, or tag..."
              className="w-full h-11 pl-10 pr-3 bg-[#12121A] border border-white/10 rounded-lg text-sm placeholder-zinc-500 focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple transition-all"
            />
          </div>
        </form>
      </div>

      {routeCategory && getCategoryIntro(routeCategory.id) && (
        <div className="mb-8 rounded-2xl border border-white/5 bg-[#0A0A0F] p-5 sm:p-6" data-testid="category-intro">
          <p className="text-sm sm:text-base text-zinc-300 leading-relaxed">
            {getCategoryIntro(routeCategory.id).intro}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="inline-flex items-center gap-2 text-xs text-zinc-500">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        <Select
          value={category || "all"}
          onValueChange={(v) => {
            // On /servers/category/:id, `category` is locked to the route param,
            // so applyParam() would be a no-op. Navigate to the target category's
            // own route instead so the change actually takes effect.
            if (routeCategory) {
              if (v === "all") navigate("/servers");
              else navigate(`/servers/category/${v}`);
              return;
            }
            applyParam("category", v === "all" ? "" : v);
          }}
        >
          <SelectTrigger data-testid="filter-category" className="w-[180px] bg-[#12121A] border-white/10 text-white">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0F] border-white/10 text-white">
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sort} onValueChange={(v) => applyParam("sort", v)}>
          <SelectTrigger data-testid="filter-sort" className="w-[180px] bg-[#12121A] border-white/10 text-white">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0F] border-white/10 text-white">
            <SelectItem value="trending">Trending</SelectItem>
            <SelectItem value="new">Newest</SelectItem>
            <SelectItem value="rating">Highest rated</SelectItem>
            <SelectItem value="members">Most members</SelectItem>
          </SelectContent>
        </Select>

        {!routeFeatured && (
          <button
            data-testid="filter-featured-toggle"
            onClick={() => navigate("/servers/featured")}
            className="h-9 px-3 rounded-lg text-sm font-semibold border transition-all bg-[#12121A] border-white/10 text-zinc-400 hover:text-neon-cyan hover:border-neon-cyan/50"
          >
            <Sparkles className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
            Featured only
          </button>
        )}
        {routeFeatured && (
          <button
            data-testid="clear-featured-btn"
            onClick={() => navigate("/servers")}
            className="h-9 px-3 rounded-lg text-sm font-semibold border transition-all bg-neon-cyan/10 border-neon-cyan/50 text-neon-cyan hover:bg-neon-cyan/20"
          >
            <Sparkles className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
            Showing featured · view all
          </button>
        )}

        {activeFilters.length > 0 && (
          <button
            onClick={() => {
              setParams(new URLSearchParams());
              setQ("");
            }}
            className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1"
            data-testid="clear-filters-btn"
          >
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-72 rounded-2xl bg-white/5" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-white/10 rounded-2xl" data-testid="browse-empty">
          <h3 className="font-heading text-xl font-bold">No servers found</h3>
          <p className="mt-2 text-sm text-zinc-500">Try a different filter — or list your own.</p>
          <button
            onClick={() => (user ? navigate("/submit") : login())}
            className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-blurple hover:bg-blurple-hover text-white font-semibold transition-all"
          >
            Submit a server
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((s, i) => (
              <ServerCard key={s.id} server={s} index={i} onBump={handleBump} />
            ))}
          </div>

          <div className="mt-8 flex flex-col items-center gap-3">
            <div className="text-xs text-zinc-500 tabular-nums" data-testid="browse-count">
              Showing <span className="text-zinc-300 font-semibold">{items.length}</span>
              {" "}of{" "}
              <span className="text-zinc-300 font-semibold">{total}</span> servers
            </div>
            {items.length < total && (
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="browse-load-more"
                className="h-11 px-6 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? "Loading…" : `Load ${Math.min(PAGE_SIZE, total - items.length)} more`}
              </button>
            )}
          </div>
        </>
      )}

      {/* Cross-links to every other category — improves crawler discoverability
          and internal PageRank distribution. Shown on category and featured pages. */}
      {(routeCategory || routeFeatured || category) && categories.length > 1 && (
        <nav className="mt-16 pt-8 border-t border-white/5" data-testid="related-categories" aria-label="Related categories">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">
            Explore other categories
          </div>
          <div className="flex flex-wrap gap-2">
            {categories
              .filter((c) => c.id !== (routeCategory?.id || category))
              .map((c) => (
                <a
                  key={c.id}
                  href={`/servers/category/${c.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/servers/category/${c.id}`);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-blurple/10 border border-white/10 hover:border-blurple/40 text-sm text-zinc-300 hover:text-white transition-all"
                >
                  {c.name}
                </a>
              ))}
          </div>
        </nav>
      )}
    </div>
  );
}
